import { t } from 'i18next'
import { z } from 'zod'
import { isValidPositionNumber, POSITION_TYPE_RANGES } from 'src/utils'
import { IPosition, IPositionType } from '../../../generated-types'

export const normalizePositionName = (raw: unknown): string =>
  String(raw ?? '')
    .trim()
    .replace(/^0+(?=\d)/, '')

export interface IPositionNameRuleContext {
  activePositions: IPosition[]
  currentPositionId?: EntityId
  isNew: boolean
  wasActive: boolean
  originalName?: string
}

export const isNumberTakenByActivePosition = (rawName: unknown, context: IPositionNameRuleContext): boolean => {
  const name = normalizePositionName(rawName)
  if (!name || !/^\d+$/.test(name)) return false

  return context.activePositions.some(
    position =>
      Number(position.id) !== Number(context.currentPositionId ?? -1) && normalizePositionName(position.name) === name,
  )
}

export const getUpdatePositionSchema = (
  positionType: IPositionType,
  context: IPositionNameRuleContext,
  isSteelCage = false,
) => {
  const maxConnectedPositions = getMaxConnectedPositions(positionType, isSteelCage) || 2
  const positionSchema = z.object({
    id: z.number().default(0),
    name: z
      .union([z.string(), z.number()])
      .transform(normalizePositionName)
      .superRefine((name, ctx) => {
        if (!name) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('positionIsRequired', { ns: 'common' }),
          })
          return
        }

        if (!/^\d+$/.test(name)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('positionNamePositiveNumbersOnly', { ns: 'position' }),
          })
          return
        }

        const num = parseInt(name, 10)
        if (positionType !== undefined && positionType !== null && POSITION_TYPE_RANGES[positionType]) {
          const { min, max } = POSITION_TYPE_RANGES[positionType]!
          if (num < min || num > max) {
            const typeLabel = t(IPositionType[positionType], { ns: 'position' })
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('positionNameRangeForType', {
                ns: 'position',
                min,
                max,
                type: typeLabel,
                defaultValue: `Position name must be between ${min} and ${max} for to be ${typeLabel} position`,
              }),
            })
            return
          }
        } else if (!isValidPositionNumber(num)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('invalidPositionName', {
              ns: 'position',
              defaultValue: 'Position name is not valid',
            }),
          })
          return
        }

        if (context.isNew && isNumberTakenByActivePosition(name, context)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('activePositionNameAlreadyExists', { ns: 'position' }),
          })
        }
      }),
    type: z.nativeEnum(IPositionType, {
      errorMap: () => {
        return { message: t('selectOneOfTheGivenOptions', { ns: 'common' }) }
      },
    }),
    positionDrawingId: z
      .number()
      .or(z.string())
      .or(z.null())
      .transform(x => (typeof x == 'string' && x == '' ? null : x))
      .optional(),
    sunkPositionDrawingId: z
      .number()
      .or(z.string())
      .or(z.null())
      .transform(x => (typeof x == 'string' && x == '' ? null : x))
      .optional(),
    isMultiPlacementsEnabled: z.boolean().default(false),
    latitude: showLatLang(positionType) ? coordinateField('latitude') : z.any(),
    longitude: showLatLang(positionType) ? coordinateField('longitude') : z.any(),
    isActive: z.boolean().default(false),
    isHidden: z.boolean().default(false),
    //width: optionalNumberField,
    mooringAnalyseAppliance: z.boolean().default(false),
    reference: z.string().or(z.null()).optional(),
    note: z.string().or(z.null()).optional(),
    mooringId: z.number().optional(),
    connectedPositions: connectedPositionsRequired(positionType)
      ? z
          .array(z.object({ id: z.number().optional(), positionId: z.number(), positionConnectedId: z.number() }))
          .min(1, { message: t('connected-position-required', { ns: 'common' }) })
          .max(maxConnectedPositions)
      : z
          .array(z.object({ id: z.number().optional(), positionId: z.number(), positionConnectedId: z.number() }))
          .max(maxConnectedPositions)
          .or(z.null())
          .optional(),
    mbl: optionalNumberField,
    accidentLoad: optionalNumberField,
    depth: optionalNumberField,
    regenerateComponents: z.boolean().optional(),
    regenerateSunkComponents: z.boolean().optional(),
    isCritical: z.boolean().default(false),
    geometry: geometrySchema,
  })

  return positionSchema.superRefine((values, ctx) => {
    if (context.isNew) return

    const name = values.name
    if (!name || !/^\d+$/.test(name)) return

    const originalName = normalizePositionName(context.originalName)

    const claimsTheNumber =
      (values.isActive && !context.wasActive) || (values.isActive && context.wasActive && name !== originalName)

    if (!claimsTheNumber) return

    if (isNumberTakenByActivePosition(name, context)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: t('activePositionNameAlreadyExists', { ns: 'position' }),
      })
    }
  })
}

export const showLatLang = (positionType: IPositionType): boolean =>
  positionType == IPositionType.MooringLine || positionType == IPositionType.Corner

export const connectedPositionsRequired = (positionType: IPositionType): boolean => {
  return positionType == IPositionType.MooringLine
}

// Number of corners each position type is linked to (see PositionsGenerator on the backend). 0 = no connected positions.
export const getMaxConnectedPositions = (positionType: IPositionType, isSteelCage = false): number => {
  switch (positionType) {
    case IPositionType.MooringLine: // 2 when the mooring line is a Y
    case IPositionType.FrameLines:
    case IPositionType.Bridle:
    case IPositionType.InnerMooringLine:
      return 2
    case IPositionType.Cage:
      return isSteelCage ? 4 : 2
    default:
      return 0
  }
}

export const coordinateField = (type: 'latitude' | 'longitude') => {
  const max = type === 'latitude' ? 90 : 180
  const invalidErrorKey = type === 'latitude' ? 'latitude_invalid_error' : 'longitude_invalid_error'
  const rangeErrorKey = type === 'latitude' ? 'latitude_range_error' : 'longitude_range_error'
  return z
    .preprocess(val => (typeof val === 'number' && isNaN(val) ? 'invalid' : val), z.union([z.string(), z.number()]))
    .superRefine((val, ctx) => {
      if (val == null || String(val).trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('provideValidInput', { ns: 'common' }) })
        return
      }
      const num = Number(val)
      if (isNaN(num)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t(invalidErrorKey, { ns: 'common' }) })
        return
      }
      if (num < -max || num > max) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t(rangeErrorKey, { ns: 'common' }) })
      }
    })
}

export const optionalNumberField = z
  .union([z.string().transform(val => (val === '' ? null : parseFloat(val))), z.number(), z.null()])
  .nullable()
  .optional()
  .refine(val => val === null || val === undefined || !isNaN(Number(val)), {
    message: t('provideValidInput', { ns: 'common' }),
  })

export const geometrySchema = z
  .object({
    name: z.string().or(z.null()).optional(),
    color: z.string().or(z.null()).optional(),
    coordinates: z
      .array(
        z.object({ order: z.number(), latitude: coordinateField('latitude'), longitude: coordinateField('longitude') }),
      )
      .default([]),
  })
  .or(z.null())
  .optional()
